import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

/**
 * Voice input via MediaRecorder + backend Whisper transcription.
 *
 * Environment gotchas handled:
 *   • Embedded iframes (Emergent preview) usually block getUserMedia even
 *     when the top-level browser has granted mic permission. We detect this
 *     and expose `inIframe: true` so the UI can offer an "Open in new tab"
 *     fallback.
 *   • Feature detection covers navigator.mediaDevices + MediaRecorder.
 *   • Errors surface actionable strings ('allow mic' | 'no mic' | generic).
 */
export function useVoice({ lang = "en-IN", apiBase } = {}) {
  const [supported, setSupported] = useState(false);
  const [inIframe, setInIframe] = useState(false);
  const [permission, setPermission] = useState("unknown"); // 'granted' | 'denied' | 'prompt' | 'unknown'
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState("");
  const [ttsMuted, setTtsMuted] = useState(false);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Detect embedded iframe
    let iframe = false;
    try { iframe = window.self !== window.top; } catch (_) { iframe = true; }
    setInIframe(iframe);

    const ok =
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof window.MediaRecorder !== "undefined";
    setSupported(ok);

    // Try to pre-check permission — this is best-effort; some browsers reject.
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "microphone" })
        .then((status) => {
          setPermission(status.state);
          status.onchange = () => setPermission(status.state);
        })
        .catch(() => {});
    }
  }, []);

  const pickMime = () => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];
    for (const t of candidates) {
      if (window.MediaRecorder && window.MediaRecorder.isTypeSupported?.(t)) return t;
    }
    return "";
  };

  const cleanup = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  };

  const start = useCallback(async () => {
    if (!supported || listening || transcribing) return;
    setError("");
    setTranscript("");

    // If we're in a cross-origin iframe, getUserMedia will very likely be blocked.
    // Tell the user what to do BEFORE we try (better UX than a cryptic reject).
    if (inIframe) {
      setError(
        "Voice booking works best on the full site — tap 'Open full site' below and try again.",
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setPermission("granted");

      const mime = pickMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      rec.onstop = async () => {
        const type = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        cleanup();
        setListening(false);
        if (blob.size < 800) {
          setError("Didn't catch anything — hold the mic and speak clearly.");
          return;
        }
        setTranscribing(true);
        try {
          const fd = new FormData();
          const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
          fd.append("file", blob, `voice.${ext}`);
          const { data } = await axios.post(`${apiBase}/voice/transcribe`, fd);
          const text = (data?.text || "").trim();
          if (!text) {
            setError("Didn't hear that — please try again.");
          } else {
            setTranscript(text);
          }
        } catch (err) {
          const detail = err?.response?.data?.detail;
          setError(typeof detail === "string" ? detail : "Transcription failed. Please try again.");
        } finally {
          setTranscribing(false);
        }
      };

      rec.onerror = (evt) => {
        // eslint-disable-next-line no-console
        console.warn("[useVoice] MediaRecorder error", evt);
        setError("Microphone error. Please try again.");
        cleanup();
        setListening(false);
      };

      rec.start();
      setListening(true);

      // Safety: auto-stop after 12s
      setTimeout(() => {
        if (rec.state === "recording") {
          try { rec.stop(); } catch (_) { /* noop */ }
        }
      }, 12000);
    } catch (err) {
      const name = err?.name || "";
      // eslint-disable-next-line no-console
      console.warn("[useVoice] getUserMedia failed", name, err?.message);
      if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
        setPermission("denied");
        setError(
          "Microphone blocked. Click the lock/mic icon next to the address bar and choose 'Allow', then try again.",
        );
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setError("No microphone detected on this device.");
      } else if (name === "NotReadableError") {
        setError("Microphone is in use by another app. Please close it and try again.");
      } else {
        setError("Voice input isn't available right now. Try again in a moment.");
      }
      cleanup();
      setListening(false);
    }
  }, [supported, listening, transcribing, apiBase, inIframe]);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state === "recording") {
      try { rec.stop(); } catch (_) { /* noop */ }
    } else {
      cleanup();
      setListening(false);
    }
  }, []);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  const speak = useCallback((text) => {
    if (ttsMuted) return;
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = lang;
      utter.rate = 1;
      utter.pitch = 1;
      window.speechSynthesis.speak(utter);
    } catch (_) { /* noop */ }
  }, [lang, ttsMuted]);

  const stopSpeaking = useCallback(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
    }
  }, []);

  const toggleMute = useCallback(() => {
    setTtsMuted((v) => {
      if (!v) stopSpeaking();
      return !v;
    });
  }, [stopSpeaking]);

  const openTopLevel = useCallback(() => {
    if (typeof window === "undefined") return;
    const url = window.location.href;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  return {
    supported,
    inIframe,
    permission,
    listening,
    transcribing,
    transcript,
    error,
    ttsMuted,
    start,
    stop,
    toggle,
    speak,
    stopSpeaking,
    toggleMute,
    openTopLevel,
    resetTranscript: () => { setTranscript(""); setError(""); },
  };
}

/**
 * Simple keyword intent classifier for the dental clinic chat.
 * Returns one of: "book" | "call" | "whatsapp" | "services" | "hours" | "unknown".
 */
export function classifyIntent(text) {
  const t = (text || "").toLowerCase().trim();
  if (!t) return "unknown";
  if (/(book|appoint|schedul|reserv|slot)/.test(t)) return "book";
  if (/(call|phone|dial|talk|ring)/.test(t)) return "call";
  if (/(whats\s?app|whatsapp|wa\b|chat on)/.test(t)) return "whatsapp";
  if (/(service|treat|price|cost|braces|implant|whiten|root canal|filling|clean|cavity|aligner|smile|veneer|orthodont|cosmetic|kid)/.test(t)) return "services";
  if (/(hour|open|close|time|location|address|where|direction|find|map|reach)/.test(t)) return "hours";
  return "unknown";
}
